import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import OnboardingTracker from "@/components/OnboardingTracker";
import { ClipboardCheck, Timer, ArrowRight } from "lucide-react";

// Simplified Agent view — "My Dealer Onboardings". Shows a per-dealer row
// with the horizontal tracker, ETA, current owner, and ACH status so the
// Agent can answer "when will my dealer be ready?" without calling Support.
export default function AgentOnboardings() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/onboarding", { params: { mine_only: true } });
      setRecords(data.records || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="agent-onboardings-page">
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Onboarding</div>
          <h1 className="font-display text-4xl font-black text-[#0B132B] leading-none mt-1">My Dealer Onboardings</h1>
          <p className="text-sm text-[#6C757D] mt-2">
            Live tracker for every dealer you signed or are helping install.
            Estimated dates are AWG's best guess — Support will notify you if anything changes.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-[#6C757D]">Loading…</div>
        ) : records.length === 0 ? (
          <Card className="p-12 text-center bg-white border-2 border-dashed border-[#E9ECEF] rounded-sm">
            <ClipboardCheck className="mx-auto text-[#6C757D]" size={28} />
            <div className="font-display text-xl font-bold text-[#0B132B] mt-3">Nothing in the pipeline yet</div>
            <div className="text-sm text-[#6C757D] mt-2">Dealers appear here the moment they sign their agreement.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {records.map((r) => <AgentRow key={r.id} rec={r} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function AgentRow({ rec }) {
  return (
    <Link to={`/onboarding/${rec.id}`} data-testid={`agent-row-${rec.id}`}>
      <Card className="p-4 bg-white border border-[#E9ECEF] rounded-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-display text-lg font-black text-[#0B132B]">{rec.dealer_name}</div>
            <div className="text-[10px] text-[#6C757D] uppercase tracking-[0.14em] font-bold">{rec.agency || "—"}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#6C757D]">Estimated Ready</div>
            <div className={`font-display text-xl font-black ${
              rec.eta?.indicator === "delayed" ? "text-red-600" :
              rec.eta?.indicator === "at_risk" ? "text-amber-600" :
              rec.eta?.indicator === "ready_early" ? "text-green-700" : "text-[#0B132B]"
            }`}>
              {rec.eta?.ready_label || "—"}
            </div>
            {rec.eta?.reason && <div className="text-[10px] text-[#6C757D] italic max-w-[220px]">{rec.eta.reason}</div>}
          </div>
        </div>
        <OnboardingTracker milestones={rec.milestones || []} />
        <div className="mt-3 pt-3 border-t border-[#F1F3F5] flex flex-wrap items-center gap-4 text-[11px] text-[#6C757D]">
          <span><b className="text-[#0B132B]">Owner:</b> {rec.current_owner?.name || "—"}</span>
          <span><b className="text-[#0B132B]">Next:</b> {rec.next_action}</span>
          {rec.ach?.status && <span><b className="text-[#0B132B]">ACH:</b> {rec.ach.status.replace(/_/g," ")}</span>}
          <span className="ml-auto text-[10px] uppercase tracking-[0.14em] font-bold text-[#D4AF37]">Open <ArrowRight size={9} className="inline"/></span>
        </div>
      </Card>
    </Link>
  );
}
